<template>
  <div class="bg-white shadow-md rounded-xl py-8 px-6">
    <div class="max-w-4xl mx-auto">
      <!-- Header -->
      <div class="mb-8">
        <h1 class="text-2xl font-bold text-gray-800">System Settings</h1>
        <p class="text-sm text-gray-500 mt-1">
          Manage system-wide settings for sales, deposits, and maintenance mode.
        </p>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="flex justify-center items-center py-12">
        <svg class="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span class="ml-3 text-gray-600">Loading settings...</span>
      </div>

      <!-- Settings Content -->
      <div v-else>
        <!-- Last Updated Info -->
        <div v-if="settings.updated_at" class="bg-gray-50 border rounded-lg p-4 mb-6">
          <div class="flex items-center justify-between">
            <div class="flex items-center text-sm text-gray-600">
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              Last updated: {{ settings.updated_at | moment('DD MMM YYYY HH:mm') }}
              <span v-if="settings.updated_by" class="ml-2">
                by <strong>{{ settings.updated_by.name }}</strong>
              </span>
            </div>
            <button
              @click="loadSettings"
              class="text-blue-600 hover:text-blue-800 text-sm flex items-center"
            >
              <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
              Refresh
            </button>
          </div>
        </div>

        <!-- Maintenance Mode Alert -->
        <div v-if="settings.maintenance_mode" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div class="flex items-start">
            <svg class="w-5 h-5 text-red-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
            </svg>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800">Maintenance Mode is Active</h3>
              <p class="text-sm text-red-700 mt-1">{{ settings.maintenance_message || 'No message set' }}</p>
            </div>
          </div>
        </div>

        <!-- Sales Settings Section -->
        <div class="mb-8">
          <h2 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <svg class="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
            </svg>
            Sales Settings
          </h2>
          <div class="space-y-4">
            <!-- Branch Sales Toggle -->
            <div class="bg-white border rounded-lg p-4 flex items-center justify-between">
              <div class="flex-1">
                <div class="flex items-center">
                  <span class="text-sm font-medium text-gray-800">Branch Sales</span>
                </div>
                <p class="text-xs text-gray-500 mt-1">Enable or disable cashpower sales from branches</p>
              </div>
              <div class="ml-4">
                <button
                  @click="toggleSetting('branch_sales_enabled')"
                  :disabled="toggleLoading === 'branch_sales_enabled'"
                  class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                  :class="settings.branch_sales_enabled ? 'bg-green-500' : 'bg-gray-300'"
                >
                  <span
                    class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                    :class="settings.branch_sales_enabled ? 'translate-x-5' : 'translate-x-0'"
                  >
                    <svg v-if="toggleLoading === 'branch_sales_enabled'" class="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </span>
                </button>
              </div>
            </div>

            <!-- Agent Sales Toggle -->
            <div class="bg-white border rounded-lg p-4 flex items-center justify-between">
              <div class="flex-1">
                <div class="flex items-center">
                  <span class="text-sm font-medium text-gray-800">Agent Sales</span>
                </div>
                <p class="text-xs text-gray-500 mt-1">Enable or disable cashpower sales from agents</p>
              </div>
              <div class="ml-4">
                <button
                  @click="toggleSetting('agent_sales_enabled')"
                  :disabled="toggleLoading === 'agent_sales_enabled'"
                  class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                  :class="settings.agent_sales_enabled ? 'bg-green-500' : 'bg-gray-300'"
                >
                  <span
                    class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                    :class="settings.agent_sales_enabled ? 'translate-x-5' : 'translate-x-0'"
                  >
                    <svg v-if="toggleLoading === 'agent_sales_enabled'" class="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </span>
                </button>
              </div>
            </div>

            <!-- Super Agent Sales Toggle -->
            <div class="bg-white border rounded-lg p-4 flex items-center justify-between">
              <div class="flex-1">
                <div class="flex items-center">
                  <span class="text-sm font-medium text-gray-800">Super Agent Sales</span>
                </div>
                <p class="text-xs text-gray-500 mt-1">Enable or disable cashpower sales from super agents</p>
              </div>
              <div class="ml-4">
                <button
                  @click="toggleSetting('super_agent_sales_enabled')"
                  :disabled="toggleLoading === 'super_agent_sales_enabled'"
                  class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                  :class="settings.super_agent_sales_enabled ? 'bg-green-500' : 'bg-gray-300'"
                >
                  <span
                    class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                    :class="settings.super_agent_sales_enabled ? 'translate-x-5' : 'translate-x-0'"
                  >
                    <svg v-if="toggleLoading === 'super_agent_sales_enabled'" class="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Deposits Settings Section -->
        <div class="mb-8">
          <h2 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <svg class="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"/>
            </svg>
            Deposit Settings
          </h2>
          <div class="space-y-4">
            <!-- Agent Deposits Toggle -->
            <div class="bg-white border rounded-lg p-4 flex items-center justify-between">
              <div class="flex-1">
                <div class="flex items-center">
                  <span class="text-sm font-medium text-gray-800">Agent Deposits</span>
                </div>
                <p class="text-xs text-gray-500 mt-1">Enable or disable agent deposit functionality</p>
              </div>
              <div class="ml-4">
                <button
                  @click="toggleSetting('agent_deposits_enabled')"
                  :disabled="toggleLoading === 'agent_deposits_enabled'"
                  class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                  :class="settings.agent_deposits_enabled ? 'bg-green-500' : 'bg-gray-300'"
                >
                  <span
                    class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                    :class="settings.agent_deposits_enabled ? 'translate-x-5' : 'translate-x-0'"
                  >
                    <svg v-if="toggleLoading === 'agent_deposits_enabled'" class="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </span>
                </button>
              </div>
            </div>

          
          </div>
        </div>

        <!-- Maintenance Settings Section -->
        <div class="mb-8">
          <h2 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <svg class="w-5 h-5 mr-2 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
            </svg>
            Maintenance Settings
          </h2>
          <div class="space-y-4">
            <!-- Maintenance Mode Toggle -->
            <div class="bg-white border rounded-lg p-4 flex items-center justify-between">
              <div class="flex-1">
                <div class="flex items-center">
                  <span class="text-sm font-medium text-gray-800">Maintenance Mode</span>
                  <span class="ml-2 px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full">Critical</span>
                </div>
                <p class="text-xs text-gray-500 mt-1">Enable system-wide maintenance mode. Users will see the maintenance message.</p>
              </div>
              <div class="ml-4">
                <button
                  @click="confirmToggle('maintenance_mode', 'Maintenance Mode')"
                  :disabled="toggleLoading === 'maintenance_mode'"
                  class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                  :class="settings.maintenance_mode ? 'bg-green-500' : 'bg-gray-300'"
                >
                  <span
                    class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                    :class="settings.maintenance_mode ? 'translate-x-5' : 'translate-x-0'"
                  >
                    <svg v-if="toggleLoading === 'maintenance_mode'" class="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </span>
                </button>
              </div>
            </div>

            <!-- Maintenance Message -->
            <div class="bg-white border rounded-lg p-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Maintenance Message
              </label>
              <textarea
                v-model="maintenanceMessage"
                rows="3"
                maxlength="500"
                class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter a message to display during maintenance..."
              ></textarea>
              <div class="flex justify-between items-center mt-2">
                <span class="text-xs text-gray-500">{{ maintenanceMessage ? maintenanceMessage.length : 0 }}/500 characters</span>
                <button
                  @click="updateMaintenanceMessage"
                  :disabled="isSavingMessage || maintenanceMessage === settings.maintenance_message"
                  class="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                >
                  <svg v-if="isSavingMessage" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {{ isSavingMessage ? 'Saving...' : 'Save Message' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Confirmation Modal -->
      <div v-if="showConfirmModal" class="fixed inset-0 z-50 overflow-y-auto">
        <div class="flex items-center justify-center min-h-screen px-4">
          <div class="fixed inset-0 bg-black opacity-50" @click="closeConfirmModal"></div>
          <div class="bg-white rounded-lg shadow-xl z-10 max-w-md w-full p-6">
            <div class="flex items-center mb-4">
              <div class="p-2 rounded-full bg-yellow-100 text-yellow-600">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-gray-800 ml-3">Confirm Action</h3>
            </div>
            <p class="text-gray-600 mb-6">
              Are you sure you want to {{ confirmAction.currentValue ? 'disable' : 'enable' }} <strong>{{ confirmAction.label }}</strong>? 
              This will affect the entire system.
            </p>
            <div class="flex justify-end space-x-3">
              <button
                @click="closeConfirmModal"
                class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                @click="executeToggle"
                class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Yes, {{ confirmAction.currentValue ? 'Disable' : 'Enable' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
    middleware: 'auth',
  data: () => ({
    isLoading: true,
    toggleLoading: null,
    isSavingMessage: false,
    showConfirmModal: false,
    confirmAction: {
      setting: null,
      label: null,
      currentValue: null
    },
    settings: {
      branch_sales_enabled: true,
      agent_sales_enabled: true,
      super_agent_sales_enabled: true,
      agent_deposits_enabled: true,
      super_agent_deposits_enabled: true,
      nawec_api_enabled: true,
      maintenance_mode: false,
      maintenance_message: null,
      updated_at: null,
      updated_by: null
    },
    maintenanceMessage: ''
  }),

  created() {
    this.loadSettings();
  },

  methods: {
    async loadSettings() {
      this.isLoading = true;
      try {
        const { data } = await axios.get('/settings');
        if (data.success) {
          this.settings = data.data;
          this.maintenanceMessage = data.data.maintenance_message || '';
        }
      } catch (e) {
        console.error('Error loading settings:', e);
        this.$toast?.error?.('Failed to load settings') || alert('Failed to load settings');
      } finally {
        this.isLoading = false;
      }
    },

    confirmToggle(setting, label) {
      this.confirmAction = {
        setting,
        label,
        currentValue: this.settings[setting]
      };
      this.showConfirmModal = true;
    },

    closeConfirmModal() {
      this.showConfirmModal = false;
      this.confirmAction = { setting: null, label: null, currentValue: null };
    },

    async executeToggle() {
      const setting = this.confirmAction.setting;
      this.closeConfirmModal();
      await this.toggleSetting(setting);
    },

    async toggleSetting(setting) {
      this.toggleLoading = setting;
      try {
        const { data } = await axios.patch(`/settings/toggle/${setting}`);
        if (data.success) {
          this.settings[setting] = data.data.value;
          this.$toast?.success?.(data.message) || console.log(data.message);
          // Refresh to get updated_at and updated_by
          await this.loadSettings();
        }
      } catch (e) {
        console.error('Error toggling setting:', e);
        const message = e.response?.data?.message || 'Failed to update setting';
        this.$toast?.error?.(message) || alert(message);
      } finally {
        this.toggleLoading = null;
      }
    },

    async updateMaintenanceMessage() {
      this.isSavingMessage = true;
      try {
        const { data } = await axios.put('/settings', {
          maintenance_message: this.maintenanceMessage || null
        });
        if (data.success) {
          this.settings = data.data;
          this.$toast?.success?.('Maintenance message updated') || console.log('Message updated');
        }
      } catch (e) {
        console.error('Error updating message:', e);
        const message = e.response?.data?.message || 'Failed to update message';
        this.$toast?.error?.(message) || alert(message);
      } finally {
        this.isSavingMessage = false;
      }
    }
  }
};
</script>
